import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Truck, Edit, Trash2, Upload, Download, FileDown, User, Car, Search } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getAuthHeaders } from "@/lib/api";
import type { Transporter, Fleet, Driver } from "../../../shared/schema";
import { insertTransporterSchema, insertFleetSchema, insertDriverSchema } from "../../../shared/schema";

// Form schemas using shared insert schemas
const transporterSchema = insertTransporterSchema.extend({
  email: z.string().email().optional().or(z.literal("")),
  status: z.enum(["active", "inactive"])
});

const fleetSchema = insertFleetSchema.extend({
  status: z.enum(["active", "maintenance", "retired"])
}).omit({ transporterId: true, tenantId: true });

const driverSchema = insertDriverSchema.extend({
  email: z.string().email().optional().or(z.literal("")),
  status: z.enum(["active", "inactive", "suspended"])
}).omit({ transporterId: true, tenantId: true });

type TransporterFormData = z.infer<typeof transporterSchema>;
type FleetFormData = z.infer<typeof fleetSchema>;
type DriverFormData = z.infer<typeof driverSchema>;

export default function Transporters() {
  const { toast } = useToast();
  const params = useParams();
  const [, setLocation] = useLocation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTransporter, setSelectedTransporter] = useState<Transporter | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [showDetailView, setShowDetailView] = useState(!!params.id);
  const [detailTransporter, setDetailTransporter] = useState<Transporter | null>(null);
  const [isAddFleetDialogOpen, setIsAddFleetDialogOpen] = useState(false);
  const [isAddDriverDialogOpen, setIsAddDriverDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Fetch transporters
  const { data: transporters = [], isLoading } = useQuery({
    queryKey: ["/api/transporters"],
  });

  // Effect to handle URL parameter changes
  useEffect(() => {
    if (params.id && transporters.length > 0) {
      const transporterId = parseInt(params.id);
      const transporter = transporters.find(t => t.id === transporterId);
      if (transporter) {
        setDetailTransporter(transporter);
        setShowDetailView(true);
      }
    } else if (!params.id) {
      setShowDetailView(false);
      setDetailTransporter(null);
    }
  }, [params.id, transporters]);

  // Fetch fleet and drivers for selected transporter
  const { data: fleet = [], isLoading: isLoadingFleet } = useQuery({
    queryKey: [`/api/fleet?transporterId=${detailTransporter?.id}`],
    enabled: !!detailTransporter?.id,
  });

  const { data: drivers = [], isLoading: isLoadingDrivers } = useQuery({
    queryKey: [`/api/drivers?transporterId=${detailTransporter?.id}`],
    enabled: !!detailTransporter?.id,
  });

  // Fetch visits (load history) for transporter drivers
  const { data: visitsData, isLoading: isLoadingVisits } = useQuery({
    queryKey: [`/api/visits`],
    enabled: !!detailTransporter?.id,
  });

  // Filter visits to only include this transporter's drivers
  const transporterVisits = visitsData?.visits?.filter((visit: any) => {
    // Check if the driver is from this transporter
    return drivers.some((driver: Driver) => driver.id === visit.driverId);
  }) || [];

  // Fetch documents for transporter drivers
  const { data: documents = [], isLoading: isLoadingDocuments } = useQuery({
    queryKey: [`/api/driver-documents`],
    enabled: !!detailTransporter?.id,
  });

  // Create transporter mutation
  const createMutation = useMutation({
    mutationFn: (data: TransporterFormData) => apiRequest("/api/transporters", "POST", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/transporters"] });
      setIsCreateDialogOpen(false);
      toast({
        title: "Success",
        description: "Transporter created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create transporter",
        variant: "destructive",
      });
    },
  });

  // Update transporter mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TransporterFormData> }) =>
      apiRequest(`/api/transporters/${id}`, "PUT", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/transporters"] });
      setIsEditDialogOpen(false);
      setSelectedTransporter(null);
      toast({
        title: "Success",
        description: "Transporter updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update transporter",
        variant: "destructive",
      });
    },
  });

  // Delete transporter mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`/api/transporters/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/transporters"] });
      toast({
        title: "Success",
        description: "Transporter deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete transporter",
        variant: "destructive",
      });
    },
  });

  // Upload Excel mutation
  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('excelFile', file);
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenant_id");
      return fetch('/api/transporters/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId || ''
        },
        body: formData,
      }).then(res => {
        if (!res.ok) throw new Error('Upload failed');
        return res.json();
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/transporters"] });
      setIsUploadDialogOpen(false);
      setUploadFile(null);
      toast({
        title: "Success",
        description: data.message || `Successfully imported ${data.data?.length || 0} transporters`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to upload file",
        variant: "destructive",
      });
    },
  });

  // Fleet mutations
  const createFleetMutation = useMutation({
    mutationFn: (data: FleetFormData & { transporterId: number }) => apiRequest("/api/fleet", "POST", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/fleet?transporterId=${detailTransporter?.id}`] });
      setIsAddFleetDialogOpen(false);
      fleetForm.reset();
      toast({
        title: "Success",
        description: "Vehicle added successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add vehicle",
        variant: "destructive",
      });
    },
  });

  // Driver mutations
  const createDriverMutation = useMutation({
    mutationFn: (data: DriverFormData & { transporterId: number }) => apiRequest("/api/drivers", "POST", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/drivers?transporterId=${detailTransporter?.id}`] });
      setIsAddDriverDialogOpen(false);
      driverForm.reset();
      toast({
        title: "Success",
        description: "Driver added successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add driver",
        variant: "destructive",
      });
    },
  });

  // Forms
  const createForm = useForm<TransporterFormData>({
    resolver: zodResolver(transporterSchema),
    defaultValues: {
      name: "",
      contactPerson: "",
      email: "",
      phone: "",
      address: "",
      registrationNumber: "",
      status: "active",
    },
  });

  const editForm = useForm<TransporterFormData>({
    resolver: zodResolver(transporterSchema),
  });

  const fleetForm = useForm<FleetFormData>({
    resolver: zodResolver(fleetSchema),
    defaultValues: {
      plateNumber: "",
      vehicleType: "",
      make: "",
      model: "",
      status: "active",
    },
  });

  const driverForm = useForm<DriverFormData>({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      employeeId: "",
      firstName: "",
      lastName: "",
      licenseNumber: "",
      licenseClass: "",
      phone: "",
      email: "",
      emergencyContact: "",
      status: "active",
    },
  });

  const onCreateSubmit = (data: TransporterFormData) => {
    createMutation.mutate(data);
  };

  const onEditSubmit = (data: TransporterFormData) => {
    if (selectedTransporter) {
      updateMutation.mutate({ id: selectedTransporter.id, data });
    }
  };

  const onFleetSubmit = (data: FleetFormData) => {
    if (detailTransporter) {
      createFleetMutation.mutate({ ...data, transporterId: detailTransporter.id });
    }
  };

  const onDriverSubmit = (data: DriverFormData) => {
    if (detailTransporter) {
      createDriverMutation.mutate({ ...data, transporterId: detailTransporter.id });
    }
  };

  const handleEdit = (transporter: Transporter) => {
    setSelectedTransporter(transporter);
    editForm.reset({
      name: transporter.name,
      contactPerson: transporter.contactPerson || "",
      email: transporter.email || "",
      phone: transporter.phone || "",
      address: transporter.address || "",
      registrationNumber: transporter.registrationNumber || "",
      status: transporter.status as "active" | "inactive",
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this transporter?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleFileUpload = () => {
    if (uploadFile) {
      uploadMutation.mutate(uploadFile);
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenant_id");
      const response = await fetch('/api/templates/transporters/csv', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId || ''
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'transporters_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download template",
        variant: "destructive",
      });
    }
  };

  const handleExportData = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenant_id");
      const response = await fetch('/api/export/transporters/csv', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId || ''
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'transporters_export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export data",
        variant: "destructive",
      });
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenant_id");
      const response = await fetch('/api/transporters/export/excel', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId || ''
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `transporters-complete-export-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download Excel template",
        variant: "destructive",
      });
    }
  };

  const handleTransporterClick = (transporter: Transporter) => {
    setLocation(`/transporters/${transporter.id}`);
  };

  const handleBackToList = () => {
    setLocation('/transporters');
  };

  // Filter transporters based on search term and status
  const filteredTransporters = (transporters as Transporter[]).filter((transporter: Transporter) => {
    // Status filter
    if (statusFilter !== "all" && transporter.status !== statusFilter) {
      return false;
    }
    
    // Search filter
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      transporter.name.toLowerCase().includes(searchLower) ||
      (transporter.contactPerson ?? "").toLowerCase().includes(searchLower) ||
      (transporter.email ?? "").toLowerCase().includes(searchLower) ||
      (transporter.phone ?? "").toLowerCase().includes(searchLower) ||
      (transporter.registrationNumber ?? "").toLowerCase().includes(searchLower)
    );
  });

  if (showDetailView && detailTransporter) {
    return (
      <div>
        <div className="space-y-6 p-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={handleBackToList}
              data-testid="button-back-to-list"
            >
              ← Back to Transporters
            </Button>
            <div>
              <h2 className="text-2xl font-bold">{detailTransporter.name}</h2>
              <p className="text-muted-foreground">Manage fleet and drivers</p>
            </div>
          </div>

          <Tabs defaultValue="transporter" className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="transporter" className="flex items-center gap-2" data-testid="tab-transporter">
                <Truck className="w-4 h-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="drivers" className="flex items-center gap-2" data-testid="tab-drivers">
                <User className="w-4 h-4" />
                Drivers
              </TabsTrigger>
              <TabsTrigger value="trucks" className="flex items-center gap-2" data-testid="tab-trucks">
                <Truck className="w-4 h-4" />
                Trucks
              </TabsTrigger>
              <TabsTrigger value="trailers" className="flex items-center gap-2" data-testid="tab-trailers">
                <Car className="w-4 h-4" />
                Trailers
              </TabsTrigger>
              <TabsTrigger value="loadhistory" className="flex items-center gap-2" data-testid="tab-load-history">
                <FileDown className="w-4 h-4" />
                Load History
              </TabsTrigger>
              <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="tab-documents">
                <Upload className="w-4 h-4" />
                Documents
              </TabsTrigger>
            </TabsList>

            {/* Transporter Details Tab */}
            <TabsContent value="transporter" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Company Information</CardTitle>
                  <CardDescription>Complete transporter details and contact information</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Company Name</label>
                        <p className="text-lg">{detailTransporter.name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Contact Person</label>
                        <p>{detailTransporter.contactPerson || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Registration Number</label>
                        <p>{detailTransporter.registrationNumber || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Email</label>
                        <p>{detailTransporter.email || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Phone</label>
                        <p>{detailTransporter.phone || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Status</label>
                        <Badge variant={detailTransporter.status === "active" ? "default" : "secondary"}>
                          {detailTransporter.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  {detailTransporter.address && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-700">Address</label>
                      <p>{detailTransporter.address}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Drivers Details Tab */}
            <TabsContent value="drivers" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Driver Management</CardTitle>
                      <CardDescription>{drivers.length} drivers registered</CardDescription>
                    </div>
                    <Button 
                      onClick={() => setIsAddDriverDialogOpen(true)}
                      data-testid="button-add-driver"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Driver
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingDrivers ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  ) : drivers.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No drivers found</p>
                      <p className="text-sm">Click "Add Driver" to get started</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>License Number</TableHead>
                          <TableHead>License Class</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {drivers.map((driver: Driver) => (
                          <TableRow key={driver.id} data-testid={`row-driver-${driver.id}`}>
                            <TableCell className="font-medium">
                              {driver.firstName} {driver.lastName}
                            </TableCell>
                            <TableCell>{driver.licenseNumber}</TableCell>
                            <TableCell>{driver.licenseClass || '-'}</TableCell>
                            <TableCell>{driver.phone || '-'}</TableCell>
                            <TableCell>{driver.email || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={driver.status === "active" ? "default" : "secondary"}>
                                {driver.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Trucks Details Tab */}
            <TabsContent value="trucks" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Truck Fleet</CardTitle>
                      <CardDescription>
                        {fleet.filter(v => v.vehicleType?.toLowerCase().includes('truck')).length} trucks registered
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setIsAddFleetDialogOpen(true)}
                      data-testid="button-add-truck"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Truck
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingFleet ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  ) : fleet.filter(v => v.vehicleType?.toLowerCase().includes('truck')).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No trucks found</p>
                      <p className="text-sm">Click "Add Truck" to get started</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Plate Number</TableHead>
                          <TableHead>Make</TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead>Year</TableHead>
                          <TableHead>Capacity (tons)</TableHead>
                          <TableHead>Tare Weight (kg)</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fleet.filter(v => v.vehicleType?.toLowerCase().includes('truck')).map((vehicle: Fleet) => (
                          <TableRow key={vehicle.id} data-testid={`row-truck-${vehicle.id}`}>
                            <TableCell className="font-medium">{vehicle.plateNumber}</TableCell>
                            <TableCell>{vehicle.make || '-'}</TableCell>
                            <TableCell>{vehicle.model || '-'}</TableCell>
                            <TableCell>{vehicle.year || '-'}</TableCell>
                            <TableCell>{vehicle.capacity || '-'}</TableCell>
                            <TableCell>{vehicle.tareWeight || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={vehicle.status === "active" ? "default" : "secondary"}>
                                {vehicle.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Trailers Details Tab */}
            <TabsContent value="trailers" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Trailer Fleet</CardTitle>
                      <CardDescription>
                        {fleet.filter(v => v.vehicleType?.toLowerCase().includes('trailer')).length} trailers registered (including link trailers)
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setIsAddFleetDialogOpen(true)}
                      data-testid="button-add-trailer"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Trailer
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingFleet ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  ) : fleet.filter(v => v.vehicleType?.toLowerCase().includes('trailer')).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No trailers found</p>
                      <p className="text-sm">Click "Add Trailer" to get started</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Plate Number</TableHead>
                          <TableHead>Trailer Type</TableHead>
                          <TableHead>Make</TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead>Year</TableHead>
                          <TableHead>Capacity (tons)</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fleet.filter(v => v.vehicleType?.toLowerCase().includes('trailer')).map((vehicle: Fleet) => (
                          <TableRow key={vehicle.id} data-testid={`row-trailer-${vehicle.id}`}>
                            <TableCell className="font-medium">{vehicle.plateNumber}</TableCell>
                            <TableCell>
                              <Badge variant={vehicle.vehicleType?.toLowerCase().includes('link') ? "outline" : "secondary"}>
                                {vehicle.vehicleType}
                                {vehicle.vehicleType?.toLowerCase().includes('link') && ' (Connected)'}
                              </Badge>
                            </TableCell>
                            <TableCell>{vehicle.make || '-'}</TableCell>
                            <TableCell>{vehicle.model || '-'}</TableCell>
                            <TableCell>{vehicle.year || '-'}</TableCell>
                            <TableCell>{vehicle.capacity || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={vehicle.status === "active" ? "default" : "secondary"}>
                                {vehicle.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Load History Tab */}
            <TabsContent value="loadhistory" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Load History</CardTitle>
                  <CardDescription>
                    {transporterVisits.length} visits for this transporter's drivers
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingVisits ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  ) : transporterVisits.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileDown className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No load history found</p>
                      <p className="text-sm">Visits will appear here when drivers from this transporter complete loads</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Driver</TableHead>
                          <TableHead>Plate Number</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Entry Weight</TableHead>
                          <TableHead>Exit Weight</TableHead>
                          <TableHead>Net Weight</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transporterVisits.map((visit: any) => {
                          const driver = drivers.find((d: Driver) => d.id === visit.driverId);
                          const netWeight = visit.weighbridgeExitWeight && visit.weighbridgeEntryWeight
                            ? (parseFloat(visit.weighbridgeExitWeight) - parseFloat(visit.weighbridgeEntryWeight)).toFixed(2)
                            : '-';
                          
                          return (
                            <TableRow key={visit.id} data-testid={`row-visit-${visit.id}`}>
                              <TableCell className="font-medium">
                                {visit.startedAt ? new Date(visit.startedAt).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell>
                                {driver ? `${driver.firstName} ${driver.lastName}` : 'Unknown Driver'}
                              </TableCell>
                              <TableCell>{visit.plateNumber || '-'}</TableCell>
                              <TableCell>{visit.customerName || '-'}</TableCell>
                              <TableCell>
                                <Badge variant={visit.status === "closed" ? "default" : "secondary"}>
                                  {visit.status}
                                </Badge>
                              </TableCell>
                              <TableCell>{visit.weighbridgeEntryWeight ? `${visit.weighbridgeEntryWeight} kg` : '-'}</TableCell>
                              <TableCell>{visit.weighbridgeExitWeight ? `${visit.weighbridgeExitWeight} kg` : '-'}</TableCell>
                              <TableCell className="font-medium">{netWeight !== '-' ? `${netWeight} kg` : '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Documents</CardTitle>
                  <CardDescription>
                    Driver documents and certificates for this transporter
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingDocuments ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  ) : documents.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Upload className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No documents found</p>
                      <p className="text-sm">Driver documents and certificates will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {documents.map((doc: any) => {
                        const driver = drivers.find((d: Driver) => d.id === doc.driverId);
                        const isExpiring = doc.expiryDate && new Date(doc.expiryDate) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // Within 30 days
                        const isExpired = doc.expiryDate && new Date(doc.expiryDate) < new Date();
                        
                        return (
                          <div key={doc.id} className="border rounded-lg p-4" data-testid={`document-${doc.id}`}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-medium">{doc.documentName || doc.documentType}</h4>
                                  <Badge variant={
                                    isExpired ? "destructive" : 
                                    isExpiring ? "outline" : 
                                    "default"
                                  }>
                                    {isExpired ? "Expired" : isExpiring ? "Expiring Soon" : "Valid"}
                                  </Badge>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                                  <div>
                                    <span className="font-medium">Driver:</span>{' '}
                                    {driver ? `${driver.firstName} ${driver.lastName}` : 'Unknown Driver'}
                                  </div>
                                  <div>
                                    <span className="font-medium">Document Type:</span>{' '}
                                    {doc.documentType}
                                  </div>
                                  <div>
                                    <span className="font-medium">Document Number:</span>{' '}
                                    {doc.documentNumber || 'Not specified'}
                                  </div>
                                  <div>
                                    <span className="font-medium">Expiry Date:</span>{' '}
                                    {doc.expiryDate ? new Date(doc.expiryDate).toLocaleDateString() : 'No expiry'}
                                  </div>
                                </div>
                              </div>
                              
                              {doc.fileUrl && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(doc.fileUrl, '_blank')}
                                  data-testid={`button-view-document-${doc.id}`}
                                >
                                  <FileDown className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Add Vehicle Dialog */}
          <Dialog open={isAddFleetDialogOpen} onOpenChange={setIsAddFleetDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Vehicle</DialogTitle>
                <DialogDescription>
                  Add a new vehicle to {detailTransporter.name}'s fleet
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={fleetForm.handleSubmit(onFleetSubmit)}>
                <Form {...fleetForm}>
                  <div className="space-y-4">
                    <FormField
                      control={fleetForm.control}
                      name="plateNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Plate Number</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="input-plate-number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={fleetForm.control}
                      name="vehicleType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Vehicle Type</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-vehicle-type">
                                <SelectValue placeholder="Select vehicle type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Truck">Truck</SelectItem>
                              <SelectItem value="Truck Tractor">Truck Tractor</SelectItem>
                              <SelectItem value="Tanker Truck">Tanker Truck</SelectItem>
                              <SelectItem value="Trailer">Trailer</SelectItem>
                              <SelectItem value="Semi Trailer">Semi Trailer</SelectItem>
                              <SelectItem value="Tanker Trailer">Tanker Trailer</SelectItem>
                              <SelectItem value="Link Trailer">Link Trailer (Two Connected)</SelectItem>
                              <SelectItem value="Flatbed Trailer">Flatbed Trailer</SelectItem>
                              <SelectItem value="Container Trailer">Container Trailer</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={fleetForm.control}
                        name="make"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Make</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value || ''} data-testid="input-make" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={fleetForm.control}
                        name="model"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Model</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value || ''} data-testid="input-model" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={fleetForm.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-vehicle-status">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="maintenance">Maintenance</SelectItem>
                              <SelectItem value="retired">Retired</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={createFleetMutation.isPending}
                      data-testid="button-submit-vehicle"
                    >
                      {createFleetMutation.isPending ? "Adding..." : "Add Vehicle"}
                    </Button>
                  </DialogFooter>
                </Form>
              </form>
            </DialogContent>
          </Dialog>

          {/* Add Driver Dialog */}
          <Dialog open={isAddDriverDialogOpen} onOpenChange={setIsAddDriverDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Driver</DialogTitle>
                <DialogDescription>
                  Add a new driver to {detailTransporter.name}'s team
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={driverForm.handleSubmit(onDriverSubmit)}>
                <Form {...driverForm}>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={driverForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name</FormLabel>
                            <FormControl>
                              <Input {...field} data-testid="input-first-name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={driverForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name</FormLabel>
                            <FormControl>
                              <Input {...field} data-testid="input-last-name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={driverForm.control}
                      name="licenseNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>License Number</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="input-license-number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={driverForm.control}
                        name="licenseClass"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>License Class</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value || ''} placeholder="e.g., A, B, C" data-testid="input-license-class" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={driverForm.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value || ''} data-testid="input-driver-phone" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={driverForm.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-driver-status">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                              <SelectItem value="suspended">Suspended</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={createDriverMutation.isPending}
                      data-testid="button-submit-driver"
                    >
                      {createDriverMutation.isPending ? "Adding..." : "Add Driver"}
                    </Button>
                  </DialogFooter>
                </Form>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-6 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold" data-testid="text-page-title">Transporters</h1>
            <p className="text-muted-foreground">Manage transportation companies</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleExportExcel}
              data-testid="button-download-excel"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Excel Template
            </Button>
            <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="button-upload-excel">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Excel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Upload 4-Tab Excel File</DialogTitle>
                  <DialogDescription>
                    Upload an Excel file with 4 tabs (Transporters, Drivers, Trucks, Trailers) to bulk import all data. Download the template first.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      data-testid="input-excel-file"
                    />
                  </div>
                  {uploadFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: {uploadFile.name}
                    </p>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleFileUpload}
                    disabled={!uploadFile || uploadMutation.isPending}
                    data-testid="button-confirm-upload"
                  >
                    {uploadMutation.isPending ? "Uploading..." : "Upload"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-create-transporter">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Transporter
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Transporter</DialogTitle>
                  <DialogDescription>
                    Add a new transportation company to the system.
                  </DialogDescription>
                </DialogHeader>
              <Form {...createForm}>
                <form onSubmit={createForm.handleSubmit(onCreateSubmit)} className="space-y-4">
                  <FormField
                    control={createForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name</FormLabel>
                        <FormControl>
                          <Input placeholder="ABC Transport Ltd" {...field} data-testid="input-transporter-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createForm.control}
                    name="contactPerson"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Person</FormLabel>
                        <FormControl>
                          <Input placeholder="John Doe" {...field} data-testid="input-contact-person" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="contact@company.com" {...field} data-testid="input-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <Input placeholder="+1234567890" {...field} data-testid="input-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createForm.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Company address" {...field} data-testid="input-address" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createForm.control}
                    name="registrationNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Registration Number</FormLabel>
                        <FormControl>
                          <Input placeholder="REG123456" {...field} data-testid="input-registration" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createForm.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-status">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={createMutation.isPending}
                      data-testid="button-submit-create"
                    >
                      {createMutation.isPending ? "Creating..." : "Create Transporter"}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>Transportation Companies</CardTitle>
                <CardDescription>
                  {filteredTransporters.length} of {transporters.length} transportation companies
                  {searchTerm && " matching your search"}
                </CardDescription>
              </div>
            </div>
            
            {/* Search and Filter */}
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search transporters by name, contact, or registration..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-transporters"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="select-status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-4">Loading transporters...</div>
            ) : filteredTransporters.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                {searchTerm ? (
                  <>
                    <p>No transporters match your search</p>
                    <p className="text-sm">Try adjusting your search terms</p>
                  </>
                ) : (
                  <>
                    <p>No transporters found</p>
                    <p className="text-sm">Click "Add Transporter" to get started</p>
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTransporters.map((transporter: Transporter) => (
                  <div
                    key={transporter.id}
                    className="p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                    data-testid={`card-transporter-${transporter.id}`}
                    onClick={() => handleTransporterClick(transporter)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium" data-testid={`text-transporter-name-${transporter.id}`}>
                        {transporter.name}
                      </h3>
                      <Badge variant={transporter.status === "active" ? "default" : "secondary"}>
                        {transporter.status}
                      </Badge>
                    </div>
                    
                    {transporter.contactPerson && (
                      <p className="text-sm text-muted-foreground mb-1">
                        Contact: {transporter.contactPerson}
                      </p>
                    )}
                    
                    {transporter.email && (
                      <p className="text-sm text-muted-foreground mb-1">
                        {transporter.email}
                      </p>
                    )}
                    
                    {transporter.phone && (
                      <p className="text-sm text-muted-foreground mb-1">
                        {transporter.phone}
                      </p>
                    )}
                    
                    {transporter.address && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {transporter.address}
                      </p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(transporter);
                        }}
                        data-testid={`button-edit-${transporter.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(transporter.id);
                        }}
                        data-testid={`button-delete-${transporter.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Transporter</DialogTitle>
              <DialogDescription>
                Update transporter information.
              </DialogDescription>
            </DialogHeader>
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
                <FormField
                  control={editForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input placeholder="ABC Transport Ltd" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="contactPerson"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Person</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="contact@company.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="+1234567890" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Company address" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="registrationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Registration Number</FormLabel>
                      <FormControl>
                        <Input placeholder="REG123456" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? "Updating..." : "Update Transporter"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}